import React, {
  useContext, useRef, useState, useEffect, useCallback,
} from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { UserContext } from 'core/UserContext';
import { asRem } from 'lib/css';
import { Pages } from 'core/pages';
import { friendlyDate, parseDate } from 'lib/utils';
import PropTypes from 'prop-types';
import {
  LeaveBalanceGetQuery,
  CalendarLeaveListQuery,
  CalendarEventListQuery,
  CelebrationQuery,
} from './leave/query';

const HomeWrapper = styled.div`
  padding: ${asRem(32)} ${asRem(40)} ${asRem(100)};
  max-width: ${asRem(960)};
  margin: 0 auto;
  font-family: 'DM Sans', -apple-system, sans-serif;

  @media (max-width: ${asRem(600)}) {
    padding: ${asRem(24)} ${asRem(20)} ${asRem(100)};
  }

  /* ── Greeting ── */
  .greeting {
    margin-bottom: ${asRem(28)};
    animation: fadeUp 0.5s ease forwards;

    .greeting-name {
      font-family: 'DM Serif Display', serif;
      font-size: ${asRem(36)};
      color: #f0eff4;
      font-weight: 400;
      line-height: 1.2;
      margin-bottom: ${asRem(6)};
    }

    .greeting-sub {
      font-size: ${asRem(14)};
      color: #6b697a;
      font-weight: 400;
      letter-spacing: 0.2px;
    }
  }

  /* ── Leave Balance ── */
  .leave-balance-card {
    padding: 0 0 ${asRem(28)};
    margin-bottom: ${asRem(28)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${asRem(16)};
    border-bottom: 1px solid #2a2a32;
    animation: fadeUp 0.5s ease forwards;
    animation-delay: 0.05s;
    opacity: 0;

    .leave-balance-left {
      display: flex;
      flex-direction: column;
      gap: ${asRem(16)};
    }

    .leave-balance-label {
      font-size: ${asRem(11)};
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #7c7a8a;
      font-weight: 500;
      margin-bottom: ${asRem(4)};
    }

    .leave-balance-row {
      display: flex;
      align-items: baseline;
      gap: ${asRem(8)};
    }

    .leave-balance-number {
      font-family: 'DM Serif Display', serif;
      font-size: ${asRem(52)};
      color: #f0eff4;
      line-height: 1;
      font-weight: 400;
    }

    .leave-balance-sub {
      font-size: ${asRem(15)};
      color: #9d9ba6;
    }

    .request-leave-btn {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(8)};
      padding: ${asRem(10)} ${asRem(20)};
      background: #9b7aef;
      color: #fff;
      border: none;
      border-radius: ${asRem(10)};
      font-size: ${asRem(13)};
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      font-family: inherit;
      letter-spacing: 0.2px;
      text-decoration: none;

      &:hover {
        background: #b49af5;
        transform: translateY(-1px);
        box-shadow: 0 4px 20px rgba(155, 122, 239, 0.3);
      }

      svg {
        width: ${asRem(16)};
        height: ${asRem(16)};
      }
    }
  }

  /* ── Section Headers ── */
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${asRem(16)};

    .section-title {
      font-size: ${asRem(11)};
      text-transform: uppercase;
      letter-spacing: 1.2px;
      color: #7c7a8a;
      font-weight: 500;
    }

    .section-action {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(4)};
      font-size: ${asRem(12)};
      color: #9b7aef;
      font-weight: 500;
      cursor: pointer;
      text-decoration: none;
      padding: ${asRem(4)} ${asRem(10)};
      border-radius: ${asRem(6)};
      transition: background 0.2s, color 0.2s;

      &:hover {
        background: rgba(155, 122, 239, 0.12);
        color: #b49af5;
      }

      svg {
        width: ${asRem(14)};
        height: ${asRem(14)};
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(${asRem(2)});
      }
    }
  }

  /* ── Leave Cards ── */
  .leaves-section {
    animation: fadeUp 0.5s ease forwards;
    animation-delay: 0.1s;
    opacity: 0;
    margin-bottom: ${asRem(36)};
    padding-bottom: ${asRem(36)};
    border-bottom: 1px solid #1e1e24;
  }

  .leaves-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${asRem(12)};

    @media (max-width: ${asRem(768)}) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${asRem(480)}) {
      grid-template-columns: 1fr;
    }
  }

  .leave-card {
    background: #151518;
    border: 1px solid #2a2a32;
    border-radius: ${asRem(14)};
    padding: ${asRem(14)} ${asRem(16)};
    transition: all 0.2s;
    cursor: pointer;
    text-decoration: none;
    display: block;

    &:hover {
      border-color: #6b5aad;
      background: #1a1a1f;
    }

    .leave-card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: ${asRem(10)};
    }

    .leave-card-date {
      font-size: ${asRem(15)};
      font-weight: 600;
      color: #f0eff4;
    }

    .leave-duration {
      font-size: ${asRem(12)};
      color: #9d9ba6;
      background: #1e1e24;
      padding: ${asRem(4)} ${asRem(10)};
      border-radius: ${asRem(6)};
      font-weight: 500;
      white-space: nowrap;
    }

    .leave-card-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .leave-type {
      display: flex;
      align-items: center;
      gap: ${asRem(8)};
      font-size: ${asRem(13)};
      color: #9d9ba6;
    }

    .leave-type-dot {
      width: ${asRem(7)};
      height: ${asRem(7)};
      border-radius: 50%;
      flex-shrink: 0;

      &.casual { background: #9b7aef; }
      &.sick { background: #6b9fff; }
      &.vacation { background: #f59e0b; }
      &.earned { background: #4ade80; }
      &.compoff { background: #e85d9c; }
    }

    .leave-status {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(4)};
      font-size: ${asRem(11)};
      font-weight: 600;
      padding: ${asRem(3)} ${asRem(8)};
      border-radius: ${asRem(5)};

      &.approved {
        color: #4ade80;
        background: rgba(74, 222, 128, 0.1);
      }

      &.pending {
        color: #e0a94e;
        background: rgba(224, 169, 78, 0.08);
      }

      &.cancelled {
        color: #ef4444;
        background: rgba(239, 68, 68, 0.08);
      }

      &.rejected {
        color: #ef4444;
        background: rgba(239, 68, 68, 0.08);
      }

      svg {
        width: ${asRem(11)};
        height: ${asRem(11)};
      }
    }
  }

  .empty-state {
    padding: ${asRem(24)} ${asRem(32)};
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${asRem(16)};

    .empty-illustration {
      width: ${asRem(80)};
      height: ${asRem(80)};

      svg {
        width: 100%;
        height: 100%;
      }
    }

    .empty-text {
      .empty-title {
        font-size: ${asRem(16)};
        font-weight: 600;
        color: #f0eff4;
        margin-bottom: ${asRem(4)};
      }

      .empty-desc {
        font-size: ${asRem(14)};
        color: #6b697a;
      }
    }
  }

  /* ── Scrollable Section Wrapper ── */
  .scroll-section {
    position: relative;

    &:hover .scroll-arrow {
      opacity: 1;
    }

  }

  .scroll-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(12, 12, 14, 0.6);
    border: none;
    color: #6b697a;
    cursor: pointer;
    z-index: 3;
    opacity: 0;
    transition: opacity 0.25s, color 0.2s, background 0.2s;
    padding: ${asRem(8)};
    border-radius: ${asRem(8)};

    &:hover {
      color: #f0eff4;
      background: rgba(12, 12, 14, 0.85);
    }

    &.left {
      left: ${asRem(4)};
    }

    &.right {
      right: ${asRem(4)};
    }

    &.hidden {
      pointer-events: none;
      opacity: 0 !important;
    }

    svg {
      width: ${asRem(20)};
      height: ${asRem(20)};
      display: block;
    }
  }

  .scroll-track {
    display: flex;
    gap: ${asRem(12)};
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: ${asRem(12)} ${asRem(4)};

    &::-webkit-scrollbar {
      display: none;
    }

    &.fade-right {
      -webkit-mask-image: linear-gradient(to right, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to right, black calc(100% - ${asRem(60)}), transparent);
    }

    &.fade-left {
      -webkit-mask-image: linear-gradient(to left, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to left, black calc(100% - ${asRem(60)}), transparent);
    }

    &.fade-both {
      -webkit-mask-image: linear-gradient(to right, transparent, black ${asRem(60)}, black calc(100% - ${asRem(60)}), transparent);
      mask-image: linear-gradient(to right, transparent, black ${asRem(60)}, black calc(100% - ${asRem(60)}), transparent);
    }
  }

  /* ── Holidays ── */
  .holidays-section {
    animation: fadeUp 0.5s ease forwards;
    animation-delay: 0.2s;
    opacity: 0;
    margin-bottom: ${asRem(36)};
  }

  .holiday-card {
    background: #151518;
    border: 1px solid #2a2a32;
    border-radius: ${asRem(14)};
    padding: ${asRem(18)} ${asRem(20)};
    min-width: ${asRem(180)};
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    transition: background 0.25s ease, border-color 0.25s ease;
    cursor: pointer;

    &:hover {
      background: #1a1a1f;
      border-color: #3a3a44;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: #9b7aef;
      border-radius: 0 0 3px 3px;
      transition: height 0.25s ease;
    }

    &:hover::before {
      height: 5px;
    }

    &.optional::before {
      background: #2dd4bf;
    }

    .holiday-date-line {
      font-size: ${asRem(14)};
      font-weight: 600;
      color: #9b7aef;
      margin-bottom: ${asRem(12)};
    }

    &.optional .holiday-date-line {
      color: #2dd4bf;
    }

    .holiday-name {
      font-size: ${asRem(15)};
      color: #f0eff4;
      font-weight: 600;
      margin-bottom: ${asRem(6)};
      line-height: 1.3;
    }

    .holiday-tag {
      font-size: ${asRem(10)};
      color: #6b697a;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 500;
    }
  }

  /* ── Celebrations ── */
  .celebrations-section {
    animation: fadeUp 0.5s ease forwards;
    animation-delay: 0.3s;
    opacity: 0;
    margin-bottom: ${asRem(32)};
  }

  .celeb-card {
    background: #18191f;
    border-radius: ${asRem(14)};
    border: 1px solid rgba(45, 212, 191, 0.3);
    padding: ${asRem(24)} ${asRem(16)} ${asRem(20)};
    text-align: center;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: ${asRem(166)};
    flex-shrink: 0;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    cursor: pointer;
    box-shadow:
      0 0 ${asRem(8)} rgba(45, 212, 191, 0.12),
      0 0 ${asRem(20)} rgba(45, 212, 191, 0.06);

    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: #2dd4bf;
      border-radius: 0 0 3px 3px;
    }

    &:hover {
      transform: translateY(${asRem(-4)}) scale(1.03);
      border-color: rgba(45, 212, 191, 0.7);
      box-shadow:
        0 0 ${asRem(14)} rgba(45, 212, 191, 0.25),
        0 0 ${asRem(28)} rgba(45, 212, 191, 0.12),
        0 ${asRem(8)} ${asRem(24)} rgba(45, 212, 191, 0.12);
    }

    &.birthday {
      border-color: rgba(155, 122, 239, 0.3);
      box-shadow:
        0 0 ${asRem(8)} rgba(155, 122, 239, 0.12),
        0 0 ${asRem(20)} rgba(155, 122, 239, 0.06);
    }

    &.birthday::before {
      background: #9b7aef;
    }

    &.birthday:hover {
      border-color: rgba(155, 122, 239, 0.7);
      box-shadow:
        0 0 ${asRem(14)} rgba(155, 122, 239, 0.25),
        0 0 ${asRem(28)} rgba(155, 122, 239, 0.12),
        0 ${asRem(8)} ${asRem(24)} rgba(155, 122, 239, 0.12);
    }

    .celeb-icon {
      width: ${asRem(56)};
      height: ${asRem(56)};
      border-radius: 50%;
      margin: 0 auto ${asRem(12)};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${asRem(28)};
      position: relative;
      z-index: 1;

      &.teal { background: rgba(45, 212, 191, 0.12); }
      &.purple { background: rgba(155, 122, 239, 0.12); }
    }

    .celeb-name {
      font-size: ${asRem(14)};
      font-weight: 600;
      color: #f0eff4;
      margin-bottom: ${asRem(2)};
      position: relative;
      z-index: 1;
    }

    .celeb-date {
      font-size: ${asRem(12)};
      color: #6b697a;
      margin-bottom: ${asRem(10)};
      position: relative;
      z-index: 1;
    }

    .celeb-badge {
      display: inline-flex;
      align-items: center;
      gap: ${asRem(3)};
      font-size: ${asRem(11)};
      font-weight: 600;
      padding: ${asRem(3)} ${asRem(8)};
      border-radius: ${asRem(5)};
      position: relative;
      z-index: 1;

      &.anniversary {
        color: #2dd4bf;
        background: rgba(45, 212, 191, 0.08);
        border: 1px solid rgba(45, 212, 191, 0.22);
      }

      &.bday {
        color: #b49af5;
        background: rgba(155, 122, 239, 0.08);
        border: 1px solid rgba(155, 122, 239, 0.22);
      }
    }
  }

  /* ── Animations ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes confettiFall {
    0% {
      opacity: 1;
      transform: translate(0, 0) rotate(0deg);
    }
    100% {
      opacity: 0;
      transform: translate(var(--drift), var(--fall)) rotate(var(--spin));
    }
  }
`;

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function getFormattedDate() {
  const now = new Date();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[now.getDay()]}, ${String(now.getDate()).padStart(2, '0')} ${months[now.getMonth()]} ${now.getFullYear()}`;
}

function getLeaveKindClass(kind) {
  if (!kind) return 'casual';
  const k = kind.toLowerCase();
  if (k.includes('sick')) return 'sick';
  if (k.includes('vacation') || k.includes('privilege')) return 'vacation';
  if (k.includes('earned')) return 'earned';
  if (k.includes('comp')) return 'compoff';
  return 'casual';
}

function getLeaveKindLabel(kind) {
  if (!kind) return 'Leave';
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function ScrollableSection({ children, fitCount }) {
  const trackRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const [cardStyle, setCardStyle] = useState(null);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const measureCards = useCallback(() => {
    const el = trackRef.current;
    if (!el || !fitCount) return;
    const gap = 12;
    const padding = 8; // 4px each side
    const available = el.clientWidth - padding;
    const w = (available - (fitCount - 1) * gap) / fitCount;
    // On small screens, don't force fitCount — let cards scroll naturally
    if (w < 140) {
      setCardStyle(null);
    } else {
      setCardStyle({ width: `${Math.floor(w)}px`, minWidth: 'unset' });
    }
    // Re-check arrows after resize
    setTimeout(updateArrows, 50);
  }, [fitCount, updateArrows]);

  useEffect(() => {
    measureCards();
    // Check arrows on mount after children render
    setTimeout(updateArrows, 100);
    window.addEventListener('resize', measureCards);
    return () => window.removeEventListener('resize', measureCards);
  }, [measureCards, updateArrows]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    const cardWidth = firstCard ? firstCard.offsetWidth + 12 : 220;
    el.scrollBy({ left: dir * cardWidth * 2, behavior: 'smooth' });
  };

  let fadeClass = '';
  if (showLeft && showRight) fadeClass = 'fade-both';
  else if (showRight) fadeClass = 'fade-right';
  else if (showLeft) fadeClass = 'fade-left';

  const styledChildren = cardStyle
    ? React.Children.map(children, (child) => (
      React.isValidElement(child)
        ? React.cloneElement(child, { style: { ...child.props.style, ...cardStyle } })
        : child
    ))
    : children;

  return (
    <div className="scroll-section">
      <button
        type="button"
        className={`scroll-arrow left${showLeft ? '' : ' hidden'}`}
        onClick={() => scroll(-1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div
        className={`scroll-track${fadeClass ? ` ${fadeClass}` : ''}`}
        ref={trackRef}
        onScroll={updateArrows}
      >
        {styledChildren}
      </div>
      <button
        type="button"
        className={`scroll-arrow right${showRight ? '' : ' hidden'}`}
        onClick={() => scroll(1)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
    </div>
  );
}

ScrollableSection.propTypes = {
  children: PropTypes.node.isRequired,
  fitCount: PropTypes.number,
};

ScrollableSection.defaultProps = {
  fitCount: null,
};

const BIRTHDAY_COLORS = ['#c084fc', '#e879f9', '#f0abfc', '#a78bfa', '#f472b6', '#fb7185'];
const ANNIVERSARY_COLORS = ['#2dd4bf', '#5eead4', '#67e8f9', '#22d3ee', '#34d399', '#6ee7b7'];

function spawnConfetti(el, kind) {
  const palette = kind === 'birthday' ? BIRTHDAY_COLORS : ANNIVERSARY_COLORS;
  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:0;overflow:hidden;';
  el.appendChild(container);
  for (let i = 0; i < 25; i += 1) {
    const piece = document.createElement('div');
    const size = 4 + Math.random() * 4;
    const color = palette[Math.floor(Math.random() * palette.length)];
    const isCircle = Math.random() > 0.5;
    const startX = Math.random() * 100;
    const driftX = (Math.random() - 0.5) * 80;
    const fallDist = 60 + Math.random() * 60;
    const rotation = Math.random() * 720;
    const delay = Math.random() * 500;
    piece.style.cssText = [
      'position:absolute;',
      `top:${asRem(-8)};`,
      `left:${startX}%;`,
      `width:${size}px;`,
      `height:${size}px;`,
      `background:${color};`,
      `border-radius:${isCircle ? '50%' : '2px'};`,
      'opacity:1;',
      `animation:confettiFall 2.5s ease-out ${delay}ms forwards;`,
      `--drift:${driftX}px;`,
      `--fall:${fallDist}px;`,
      `--spin:${rotation}deg;`,
    ].join('');
    container.appendChild(piece);
  }
  setTimeout(() => { if (container.parentNode) container.remove(); }, 3500);
}

function ConfettiCard({ kind, children, style }) {
  const cardRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    const el = cardRef.current;
    if (el) spawnConfetti(el, kind);
  }, [kind]);

  return (
    <div
      ref={cardRef}
      className={`celeb-card${kind === 'birthday' ? ' birthday' : ''}`}
      style={style}
      onMouseEnter={handleMouseEnter}
    >
      {children}
    </div>
  );
}

ConfettiCard.propTypes = {
  kind: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
};

ConfettiCard.defaultProps = {
  style: null,
};

export const Home = () => {
  const userContext = useContext(UserContext);
  const { user } = userContext;
  const userKey = user.resource.key;
  const variables = { user: { key: userKey } };

  const { data: balanceData } = useQuery(LeaveBalanceGetQuery, {
    variables: { ...(variables || {}) },
  });

  const now = new Date();
  const currentYear = now.getFullYear();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
  const leaveStart = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}-01`;
  const leaveEnd = `${nextMonthEnd.getFullYear()}-${String(nextMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(nextMonthEnd.getDate()).padStart(2, '0')}`;

  const { data: leavesData } = useQuery(CalendarLeaveListQuery, {
    variables: {
      user: { key: userKey },
      start: leaveStart,
      end: leaveEnd,
      onlyParent: true,
    },
  });
  const { data: holidaysData } = useQuery(CalendarEventListQuery, {
    variables: { year: currentYear, upcoming: true },
  });

  const { data: celebrationData } = useQuery(CelebrationQuery, {
    variables: { months: 3 },
  });

  const balance = balanceData?.staff_emp_policy_leavebalance_get?.balance?.leaves?.balance || 0;
  const allLeaves = leavesData?.staff_emp_calendar_leave?.events || [];
  const leaves = allLeaves.filter((leave) => {
    const approvers = leave.leave?.approved_by || [];
    const last = approvers[approvers.length - 1];
    return !last || (last.status !== 'Revoked' && last.status !== 'Cancelled');
  });
  const holidays = holidaysData?.staff_emp_calendar_event?.events || [];
  const allCelebrations = celebrationData?.staff_emp_celebration_celebration?.items || [];

  // Filter to current month and next month only
  const currentMonth = now.getMonth();
  const nextMonth = (currentMonth + 1) % 12;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const celebrations = allCelebrations.filter((item) => {
    const d = new Date(item.date);
    const m = d.getMonth();
    return d >= today && (m === currentMonth || m === nextMonth);
  });

  return (
    <HomeWrapper>
      {/* Greeting */}
      <div className="greeting">
        <div className="greeting-name">
          {`${getGreeting()}, ${user.profile.first_name}`}
        </div>
        <div className="greeting-sub">
          {`${getFormattedDate()} \u00B7 Roanuz`}
        </div>
      </div>

      {/* Leave Balance */}
      <div className="leave-balance-card">
        <div className="leave-balance-left">
          <div>
            <div className="leave-balance-row">
              <span className="leave-balance-number">{balance}</span>
              <span className="leave-balance-sub">Leave Balance</span>
            </div>
          </div>
          <Link to={Pages.staff.leave.create.meta.url} className="request-leave-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Request Leave
          </Link>
        </div>
      </div>

      {/* My Recent Leaves */}
      <div className="leaves-section">
        <div className="section-header">
          <div className="section-title">My Recent Leaves</div>
          <Link className="section-action" to={Pages.staff.leave.month.balance.meta.urlFor({ month: 'current' })}>
            View all
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </Link>
        </div>
        {leaves.length > 0 ? (
          <div className="leaves-grid">
            {leaves.slice(0, 6).map((leave) => {
              const isApproved = leave.status?.is_approved;
              const approvers = leave.leave?.approved_by || [];
              const lastApprover = approvers[approvers.length - 1];
              const lastStatus = lastApprover ? lastApprover.status : null;
              const isCancelled = lastStatus === 'Cancelled' || lastStatus === 'Revoked';
              const isRejected = lastStatus === 'Rejected';
              let statusClass = 'pending';
              let statusLabel = 'Pending';
              if (isCancelled) {
                statusClass = 'cancelled'; statusLabel = 'Cancelled';
              } else if (isRejected) {
                statusClass = 'rejected'; statusLabel = 'Rejected';
              } else if (isApproved) {
                statusClass = 'approved'; statusLabel = 'Approved';
              }
              let qty = leave.leave?.quantity || 1;
              if (leave.event.date_to && leave.event.date_to !== leave.event.date) {
                const d1 = new Date(leave.event.date);
                const d2 = new Date(leave.event.date_to);
                const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
                if (diff > qty) qty = diff;
              }

              return (
                <Link
                  to={`/staff/leave/view/${leave.resource.key}`}
                  className="leave-card"
                  key={leave.resource.key}
                >
                  <div className="leave-card-top">
                    <div className="leave-card-date">
                      {leave.event.date_to && leave.event.date_to !== leave.event.date
                        ? `${friendlyDate(leave.event.date, null, 'MMM DD')} – ${friendlyDate(leave.event.date_to, null, 'MMM DD')}`
                        : friendlyDate(leave.event.date)}
                    </div>
                    <div className="leave-duration">
                      {qty === 0.5 ? '0.5 days' : `${qty} day${qty > 1 ? 's' : ''}`}
                    </div>
                  </div>
                  <div className="leave-card-bottom">
                    <div className="leave-type">
                      <div className={`leave-type-dot ${getLeaveKindClass(leave.leave?.kind)}`} />
                      {getLeaveKindLabel(leave.leave?.kind)}
                    </div>
                    <div className={`leave-status ${statusClass}`}>
                      {isApproved && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                      {(isCancelled || isRejected) && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      )}
                      {(!isApproved && !isCancelled && !isRejected) && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      )}
                      {statusLabel}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-illustration">
              <svg
                viewBox="0 0 120 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Window frame */}
                <rect
                  x="14"
                  y="8"
                  width="92"
                  height="80"
                  rx="10"
                  fill="#16161a"
                  stroke="#3a3a44"
                  strokeWidth="4"
                />
                {/* Cross bars */}
                <line
                  x1="60"
                  y1="8"
                  x2="60"
                  y2="88"
                  stroke="#3a3a44"
                  strokeWidth="3.5"
                />
                <line
                  x1="14"
                  y1="48"
                  x2="106"
                  y2="48"
                  stroke="#3a3a44"
                  strokeWidth="3.5"
                />
                {/* Window sill */}
                <line
                  x1="8"
                  y1="90"
                  x2="112"
                  y2="90"
                  stroke="#3a3a44"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Top-left pane: purple dot */}
                <circle
                  cx="32"
                  cy="32"
                  r="5"
                  fill="#9b7aef"
                  opacity="0.7"
                />
                <circle
                  cx="42"
                  cy="22"
                  r="1.5"
                  fill="#6b697a"
                  opacity="0.4"
                />
                {/* Top-right pane: green dot + purple dot */}
                <circle
                  cx="76"
                  cy="22"
                  r="3"
                  fill="#4ade80"
                  opacity="0.5"
                />
                <circle
                  cx="90"
                  cy="34"
                  r="3.5"
                  fill="#9b7aef"
                  opacity="0.4"
                />
                <circle
                  cx="82"
                  cy="40"
                  r="1.5"
                  fill="#6b697a"
                  opacity="0.3"
                />
                {/* Bottom-left: teal dot */}
                <circle
                  cx="28"
                  cy="62"
                  r="2.5"
                  fill="#2dd4bf"
                  opacity="0.5"
                />
                {/* Coffee mug body */}
                <rect
                  x="40"
                  y="80"
                  width="24"
                  height="18"
                  rx="3"
                  fill="#3a3a44"
                />
                {/* Mug handle */}
                <path
                  d="M64 84 C70 84, 70 94, 64 94"
                  stroke="#3a3a44"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
                {/* Steam lines */}
                <path
                  d="M47 78 Q49 72, 47 66"
                  stroke="#9b7aef"
                  strokeWidth="1.8"
                  fill="none"
                  opacity="0.6"
                  strokeLinecap="round"
                />
                <path
                  d="M52 77 Q54 70, 52 63"
                  stroke="#9b7aef"
                  strokeWidth="1.8"
                  fill="none"
                  opacity="0.45"
                  strokeLinecap="round"
                />
                <path
                  d="M57 78 Q59 73, 57 67"
                  stroke="#9b7aef"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.3"
                  strokeLinecap="round"
                />
                {/* Bottom-right: crescent moon */}
                <circle
                  cx="84"
                  cy="70"
                  r="11"
                  fill="#2dd4bf"
                  opacity="0.5"
                />
                <circle
                  cx="89"
                  cy="65"
                  r="10"
                  fill="#16161a"
                />
                {/* Small purple dot near moon */}
                <circle
                  cx="74"
                  cy="78"
                  r="1.5"
                  fill="#9b7aef"
                  opacity="0.3"
                />
              </svg>
            </div>
            <div className="empty-text">
              <div className="empty-title">
                No recent leaves
              </div>
              <div className="empty-desc">
                Enjoy the rhythm.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Holidays */}
      <div className="holidays-section">
        <div className="section-header">
          <div className="section-title">Upcoming Holidays</div>
        </div>
        {holidays.length > 0 ? (
          <ScrollableSection>
            {holidays.map((event) => {
              const isOptional = event.event?.kind?.toLowerCase() === 'optionalholiday';
              return (
                <div
                  className={`holiday-card${isOptional ? ' optional' : ''}`}
                  key={event.resource.key}
                >
                  <div className="holiday-date-line">
                    {friendlyDate(event.event.date, null, 'MMM DD, ddd')}
                  </div>
                  <div className="holiday-name">{event.event.name}</div>
                  <div className="holiday-tag">
                    {isOptional ? 'Optional Holiday' : 'Public Holiday'}
                  </div>
                </div>
              );
            })}
          </ScrollableSection>
        ) : (
          <div className="empty-message">No upcoming holidays</div>
        )}
      </div>

      {/* Celebrations */}
      <div className="celebrations-section">
        <div className="section-header">
          <div className="section-title">Upcoming Celebrations</div>
        </div>
        {celebrations.length > 0 ? (
          <ScrollableSection fitCount={5}>
            {celebrations.map((person) => (
              <ConfettiCard kind={person.kind} key={person.emp_id}>
                <div className={`celeb-icon ${person.kind === 'birthday' ? 'purple' : 'teal'}`}>
                  {person.kind === 'birthday' ? '\uD83C\uDF82' : '\uD83C\uDFC6'}
                </div>
                <div className="celeb-name">{person.name}</div>
                <div className="celeb-date">
                  {parseDate(person.date).format('MMM DD')}
                </div>
                <div className={`celeb-badge ${person.kind === 'birthday' ? 'bday' : 'anniversary'}`}>
                  {person.kind === 'birthday'
                    ? '\uD83C\uDF82 Birthday'
                    : `${person.years >= 5 ? '\uD83C\uDFC6' : '\u2B50'} ${person.years} ${person.years === 1 ? 'year' : 'years'}`}
                </div>
              </ConfettiCard>
            ))}
          </ScrollableSection>
        ) : (
          <div className="empty-message">No celebrations this month</div>
        )}
      </div>
    </HomeWrapper>
  );
};

Home.propTypes = {
};
